import React, { useEffect, useMemo, Fragment } from 'react';
import { Link, useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button }from '@cmsgov/design-system';

import { FormattedMessage } from 'react-intl';

import Table from '@common-components/table/Table';
import Tooltip from '@common-components/tooltip/Tooltip';
import Circle from '@common-components/circle/Circle';

import { showModal } from '@actions/common/actionCreators';
import { configs } from '@constants/configs/configs';
import { ticketMgmtModalTypes } from '@constants/ticketMgmt/modals/ticketMgmtModalTypes';
import {
    getWorkflowTypes,
    getWorkgroups,
    getPermissions,
    getWorkflowsByCategory,
    selectCategory,
    renameCategory,
    saveNewTicketType 
} from '@actions/ticketMgmtPortal/ticketMgmtPortalActionCreators';
// Hooks
import { useCustomFields } from '@hooks/index';

import { 
    ticketTypeSelectOptionsLoadedSelector,
    workflowsByCategoryLoadedSelector,
    sortedWorkflowsByCategorySelector,
    activeCategorySelector,
    ticketFormOptionsSelector,
    workgroupsOptionsSelector,
} from '@selectors/ticketMgmtSelectors';

import {
    CATEGORY_FORM_NAMES,
    CATEGORY_FORM_FIELDS,
    DEFAULT_TICKET_TYPE_FORM_FIELDS,
    TICKET_TYPE_FORM_NAMES,
    TICKET_TYPE_TABLE_HEADERS,
    DEFAULT_EXPIRATION_DATE_LIMIT,
    DEFAULT_EXPIRATION_DATE,
    DEFAULT_SELECT,
    DEFAULT_DUE_DATE_LIMIT,
    STATUSES,
    PRIORITIES,
    SAVE_MODES,
    DEFAULT_WORKFLOW_TYPE,
    ENTITY_TYPES
} from './forms/ticketFormConfigs';

const TicketType = (props) => {

    const {
        activeCategory,
        optionsLoaded,
        formOptions,
        workflowsByCategoryLoaded,
        workflowsByCategory,
        workgroupsOptions,
        showModal,
        renameCategory,
        getWorkflowTypes,
        getWorkgroups,
        getPermissions,
        getWorkflowsByCategory,
        selectCategory,
        saveNewTicketType,
    } = props;
    const params = useParams();

    const { customFields=[], getCustomFields } = useCustomFields();

    useEffect(() => {
        selectCategory(params);
    }, [params]); 

    useEffect(() => {
        getCustomFields();
    },[]);

    useEffect(() => {
        !workflowsByCategoryLoaded && getWorkflowsByCategory();
    }, [workflowsByCategoryLoaded]);

    useEffect(() => {
        if (!optionsLoaded) {
            getWorkflowTypes(configs.endpoints.TICKET_MGMT_WORKFLOW_TYPES);
            getWorkgroups(configs.endpoints.TICKET_MGMT_WORKGROUPS);
            getPermissions(configs.endpoints.TICKET_MGMT_PERMISSIONS);
        }
    }, [optionsLoaded]);

    const TICKET_TYPE_FORM_FIELDS =  DEFAULT_TICKET_TYPE_FORM_FIELDS.map(field => {
        switch(field.name) {
            case TICKET_TYPE_FORM_NAMES.WORKFLOW:
                field.options = [DEFAULT_SELECT, ...formOptions.workflowTypes];
                break;
            case TICKET_TYPE_FORM_NAMES.WORKGROUP:
                field.options = workgroupsOptions;
                break;
            case TICKET_TYPE_FORM_NAMES.PERMISSION_NAME:
                field.options = [DEFAULT_SELECT, ...formOptions.permissions];
                break;
            case TICKET_TYPE_FORM_NAMES.DUE_DATE:
                field.options = [DEFAULT_SELECT, ...Array.from({ length: DEFAULT_DUE_DATE_LIMIT }, (_, i) => i + 1)];
                break;
            case TICKET_TYPE_FORM_NAMES.STATUS:
                field.options = STATUSES;
                break;
            case TICKET_TYPE_FORM_NAMES.PRIORITY:
                field.options = PRIORITIES;
                break;
        }
        return field;
    });

    const processSaveTicketTypePayload = (formValues, mode) => {
        const { id, type, filename, description, status, workgroup, expDate, permission, defaultPriority, create, open,
            complete, cancel, fileUploadEnabled, secureInbox=true, custom_fields = [], spanishName = null } = formValues;

        const payload = {
            category: activeCategory.ticketCategory,
            type,
            filename,
            description,
            status,
            queueId: parseInt(workgroup),
            expDate: parseInt(expDate),
            permission,
            fileUploadEnabled,
            customFieldDetails: custom_fields,
            default_Priority: defaultPriority,
            emailConfig: JSON.stringify({
                create, open, complete, cancel, secureInbox
            }),
            categoryEs: activeCategory.ticketCategorySpanishName ?  activeCategory.ticketCategorySpanishName : null,
            typeEs : permission === 'TKM_WORKFLOW_PUBLIC1' ? spanishName : null
        };

        switch (mode) {
            case(SAVE_MODES.EDIT):
                return { id: id, ...payload };
            case(SAVE_MODES.ADD):
                return { ...payload, filename: DEFAULT_WORKFLOW_TYPE  };
        }
    };

    const openRenameCategoryForm = () => {
        showModal({
            modalType: ticketMgmtModalTypes.TICKET_TYPE_MODAL,
            modalData: {
                className: 'ticket_management',
                headerText: 'ticket_management.rename_category',
                initialFormValues: { [CATEGORY_FORM_NAMES.NEW_CATEGORY]: activeCategory.ticketCategory, [CATEGORY_FORM_NAMES.SPANISH_NAME]: activeCategory.ticketCategorySpanishName },
                formFields: CATEGORY_FORM_FIELDS,
                entity: ENTITY_TYPES.CATEGORY,
                onSaveHandler: ( newVals) => {
                    if ((activeCategory.ticketCategory !== newVals.newCategory) || (activeCategory.ticketCategorySpanishName !== newVals.spanishName))
                        return renameCategory(activeCategory.ticketCategory, newVals.newCategory, activeCategory.ticketCategorySpanishName, newVals.spanishName );
                }
            }
        });
    };

    const ModifyCustomFieldData = (data) => {
        let updatedData = [];
        data.forEach(function(val) {
            updatedData.push({ 'id': val.id, 'name': val.name, 'type': val.type, 'selected': false , 'required' : val.required });
        });
        return updatedData;
    };

    const openTicketTypeForm = () => {

        const customFieldForAddTicketType = ModifyCustomFieldData(customFields);

        const INITIAL_TICKET_TYPE_FORM_VALUES = {
            [TICKET_TYPE_FORM_NAMES.NAME]: '',
            [TICKET_TYPE_FORM_NAMES.WORKFLOW]: DEFAULT_SELECT,
            [TICKET_TYPE_FORM_NAMES.DESCRIPTION]: '',
            [TICKET_TYPE_FORM_NAMES.STATUS]: DEFAULT_SELECT ,
            [TICKET_TYPE_FORM_NAMES.WORKGROUP]: DEFAULT_SELECT,
            [TICKET_TYPE_FORM_NAMES.DUE_DATE]: DEFAULT_SELECT,
            [TICKET_TYPE_FORM_NAMES.PERMISSION_NAME]: DEFAULT_SELECT,
            [TICKET_TYPE_FORM_NAMES.ALLOW_UPLOAD]: false,
            [TICKET_TYPE_FORM_NAMES.PRIORITY]: DEFAULT_SELECT,
            [TICKET_TYPE_FORM_NAMES.EMAIL_CONFIG.CREATE]: true,
            [TICKET_TYPE_FORM_NAMES.EMAIL_CONFIG.OPEN]: false,
            [TICKET_TYPE_FORM_NAMES.EMAIL_CONFIG.COMPLETE]: true,
            [TICKET_TYPE_FORM_NAMES.EMAIL_CONFIG.CANCEL]: true,
            [TICKET_TYPE_FORM_NAMES.CUSTOM_FIELDS]: customFieldForAddTicketType,
            [TICKET_TYPE_FORM_NAMES.SPANISH_NAME]:''
        };

        showModal({
            modalType: ticketMgmtModalTypes.TICKET_TYPE_MODAL,
            modalData: {
                className: 'ticket_management',
                headerText: 'ticket_management.add_new_ticket_type',
                initialFormValues: INITIAL_TICKET_TYPE_FORM_VALUES,
                formFields: TICKET_TYPE_FORM_FIELDS,
                entity: ENTITY_TYPES.TICKET,
                onSaveHandler: (newVals) => saveNewTicketType(processSaveTicketTypePayload(newVals, SAVE_MODES.ADD))
            }
        });
    };

    const editTicketTypeForm = (e, inx) => {
        e.preventDefault();
        const selectedTicketTypeData = workflowsByCategory[inx];

        const { id, type, typeEs, filename, description, status, workgroup, expDate, permission, defaultPriority, fileUploadEnabled,
            create=false, open=false, complete=false, cancel=false, customFields=[] } = selectedTicketTypeData;

        const INITIAL_EDIT_TICKET_TYPE_FORM_VALUES = {
            [TICKET_TYPE_FORM_NAMES.NAME]: type,
            [TICKET_TYPE_FORM_NAMES.WORKFLOW]: filename || DEFAULT_SELECT,
            [TICKET_TYPE_FORM_NAMES.DESCRIPTION]: description,
            [TICKET_TYPE_FORM_NAMES.STATUS]: STATUSES.includes(status) ?  status : DEFAULT_SELECT,
            [TICKET_TYPE_FORM_NAMES.WORKGROUP]: workgroup && workgroup.value || DEFAULT_SELECT,
            [TICKET_TYPE_FORM_NAMES.DUE_DATE]: expDate || DEFAULT_SELECT,
            [TICKET_TYPE_FORM_NAMES.PERMISSION_NAME]: permission || DEFAULT_SELECT,
            [TICKET_TYPE_FORM_NAMES.PRIORITY]: defaultPriority || DEFAULT_SELECT,
            [TICKET_TYPE_FORM_NAMES.ALLOW_UPLOAD]: fileUploadEnabled,
            [TICKET_TYPE_FORM_NAMES.EMAIL_CONFIG.CREATE]: create,
            [TICKET_TYPE_FORM_NAMES.EMAIL_CONFIG.OPEN]: open,
            [TICKET_TYPE_FORM_NAMES.EMAIL_CONFIG.COMPLETE]: complete,
            [TICKET_TYPE_FORM_NAMES.EMAIL_CONFIG.CANCEL]: cancel,
            [TICKET_TYPE_FORM_NAMES.CUSTOM_FIELDS]: customFields,
            [TICKET_TYPE_FORM_NAMES.SPANISH_NAME]: typeEs
        };

        showModal({
            modalType: ticketMgmtModalTypes.TICKET_TYPE_MODAL,
            modalData: {
                className: 'ticket_management',
                headerText: 'ticket_management.edit_ticket_type',
                initialFormValues: INITIAL_EDIT_TICKET_TYPE_FORM_VALUES,
                formFields: TICKET_TYPE_FORM_FIELDS,
                entity: ENTITY_TYPES.TICKET,
                onSaveHandler: (newVals) => saveNewTicketType(processSaveTicketTypePayload({ id, ...newVals }, SAVE_MODES.EDIT))
            }
        });
    };

    const processTableData = () => {
        if (workflowsByCategory && workflowsByCategory.length > 0) {
            return workflowsByCategory.map((row, index) => {
                return {
                    status: <Circle id={row.id} content={row.status}/>,
                    permission: row.permission || '-',
                    expDate: row.expDate ? row.expDate >= DEFAULT_EXPIRATION_DATE_LIMIT ? DEFAULT_EXPIRATION_DATE : row.expDate : '-',
                    defaultPriority: row.defaultPriority || '-',
                    workgroup: row.workgroup.label || '-',
                    type:<Fragment>
                        <Tooltip
                            id={row.type}
                            content={row.description}
                            plain={true}
                            index={`ticket-mgmt_desc_tooltip_${index}`}
                            triggerDecorationClassName='tooltip--clickable'
                            onClickHandler={(e) => editTicketTypeForm(e, index)}
                        /> 
                        <span>{row.typeEs ? ' ( '+row.typeEs+' )' : '' }</span> 
                    </Fragment>
                };
            });
        }
    };

    const memoizedTableData = useMemo(() => processTableData(), [workflowsByCategory]);

    return (
        <div className="gi-panel ticket-types">
            <div className='ticket-types__pagination'>
                <Link to={configs.routes.TICKET_MGMT_TYPES}>
                    <FormattedMessage id="ticket_management.ticket_categories"/>
                </Link>
                <FontAwesomeIcon icon="angle-right" size="1x" className="ticket-icon"/>
                <span className="ticket__category-name">{ activeCategory.ticketCategory } </span>
            </div>

            <div className="ticket-types__btn-controls">
                <Button
                    id='addNewTicketCategory'
                    className='usa-button  gi-button gi-button__secondary'
                    onClick={openRenameCategoryForm}
                >
                    <FormattedMessage id="ticket_management.rename_category"/>
                </Button>
                <Button
                    id='addNewTicketCategory'
                    className='usa-button  gi-button gi-button__primary'
                    onClick={openTicketTypeForm}
                >
                    <FormattedMessage id="ticket_management.add_new_ticket_type"/>
                </Button>
            </div>

            <div className="ticket-types__table">
                {
                    workflowsByCategory && workflowsByCategory.length > 0 &&
                    <Table data={memoizedTableData} columns={ TICKET_TYPE_TABLE_HEADERS }/>
                }
            </div>
        </div>
    );
};

const mapStateToProps = createStructuredSelector({
    workflowsByCategoryLoaded: workflowsByCategoryLoadedSelector,
    workflowsByCategory: sortedWorkflowsByCategorySelector,
    optionsLoaded: ticketTypeSelectOptionsLoadedSelector,
    formOptions: ticketFormOptionsSelector,
    activeCategory: activeCategorySelector,
    workgroupsOptions: workgroupsOptionsSelector,
});

const mapDispatchToProps = {
    showModal,
    renameCategory,
    getWorkflowTypes,
    getWorkgroups,
    getPermissions,
    getWorkflowsByCategory,
    selectCategory,
    saveNewTicketType
};

export default connect(mapStateToProps, mapDispatchToProps)(TicketType);